// Company purchases — a separate, permission-gated context. A company buyer
// (commerce:company:purchase) raises a request paid by a legal entity; an
// approver (commerce:company:approve — incl. Finance, never the requester)
// decides it BEFORE any money moves. Changing an approved request voids the
// approval. Execution stays gated (shown with the operations reason) until a
// company funding method is verified with the provider — it never falls back
// to anyone's personal wallet. Personal purchases never appear here.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Building2, Lock, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Textarea } from '@/features/shared/components/ui/textarea';
import { DataState, errorVariant } from '@/features/shared/components/ui/DataState';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/features/shared/components/ui/select';
import { PhoneInput } from '@/features/shared/components/PhoneInput';
import { PeoplePicker, type Person } from '@/features/shared/components/PeoplePicker';
import { usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';
import { listEligibleAssignees } from '@/services/workService';
import { listLegalEntities, type LegalEntity } from '@/services/payrollSettingsService';
import { formatMoney } from '@/lib/money';
import { getApiErrorMessage } from '@/lib/apiError';
import { commerceApi } from '../../api/commerceApi';
import { isCompletePhone, phoneDigits } from '../../lib/format';
import type { CompanyLineInput, CompanyRequest, OperatorOption } from '../../types';

interface LineDraft {
  key: string;
  kind: 'employee' | 'external';
  person: Person | null;
  label: string;
  phone: string;
  operator: string;
  amount: string;
}

const newLine = (): LineDraft => ({ key: Math.random().toString(36).slice(2), kind: 'employee', person: null, label: '', phone: '', operator: '', amount: '' });

const STATUS_BADGE: Record<string, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  draft: 'neutral', submitted: 'info', approved: 'success', rejected: 'danger', cancelled: 'neutral',
  executing: 'info', completed: 'success', partially_completed: 'warning',
};

interface Props {
  operators: OperatorOption[];
  canPurchase: boolean;
  canApprove: boolean;
  currentUserId?: string;
}

export const CompanyPurchasesPanel: React.FC<Props> = ({ operators, canPurchase, canApprove, currentUserId }) => {
  const { t } = useTranslation('everydayServices');
  const prompt = usePrompt();
  const [list, setList] = useState<CompanyRequest[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [entities, setEntities] = useState<LegalEntity[]>([]);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setError(null);
    setList(null);
    try { setList(await commerceApi.listCompanyRequests()); } catch (e) { setError(e); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { listLegalEntities().then(setEntities).catch(() => setEntities([])); }, []);

  // --- create form ---
  const [entityId, setEntityId] = useState('');
  const [purpose, setPurpose] = useState('');
  const [costCentre, setCostCentre] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([newLine()]);
  const [showErrors, setShowErrors] = useState(false);
  const setLine = (key: string, patch: Partial<LineDraft>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const lineOk = (l: LineDraft) => (l.kind === 'external' || !!l.person) && isCompletePhone(phoneDigits(l.phone)) && !!l.operator && Number(l.amount) > 0;
  const formOk = !!entityId && purpose.trim().length >= 3 && lines.length > 0 && lines.every(lineOk);
  const total = lines.reduce((s, l) => s + (Number(l.amount) > 0 ? Math.round(Number(l.amount)) : 0), 0);

  const create = async () => {
    if (!formOk) { setShowErrors(true); return; }
    setBusy('create');
    try {
      const payload: CompanyLineInput[] = lines.map((l) => ({
        recipientKind: l.kind,
        employeeId: l.kind === 'employee' ? l.person?.employee_id : undefined,
        recipientLabel: l.kind === 'employee' ? (l.person?.display_name ?? '') : l.label.trim(),
        recipient: phoneDigits(l.phone),
        operator: l.operator,
        productType: 'AIRTIME',
        amount: Math.round(Number(l.amount)),
      }));
      await commerceApi.createCompanyRequest({ entityId, purpose: purpose.trim(), costCentre: costCentre.trim(), currency: 'TZS', lines: payload });
      toast.success(t('company.created'));
      setCreating(false);
      setLines([newLine()]); setPurpose(''); setCostCentre(''); setShowErrors(false);
      void load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('company.saveError')), { duration: 6000 });
    } finally {
      setBusy('');
    }
  };

  const act = async (r: CompanyRequest, action: 'submit' | 'approve' | 'reject' | 'cancel' | 'execute') => {
    let note: string | undefined;
    if (action === 'reject') {
      const v = await prompt({ title: t('company.rejectTitle'), label: t('company.rejectReason'), confirmLabel: t('company.reject'),
        cancelLabel: t('confirm.cancel'), multiline: true, required: true, minLength: 3, danger: true });
      if (v == null || String(v).trim().length < 3) return;
      note = String(v).trim();
    }
    setBusy(r.id + action);
    try {
      await commerceApi.companyAction(r.id, action, note);
      void load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('company.actionError')), { duration: 7000 });
    } finally {
      setBusy('');
    }
  };

  const money = (n: number, c = 'TZS') => formatMoney(n, c);
  const entityName = (id: string) => entities.find((e) => e.id === id)?.name ?? t('company.entity');
  const err = (show: boolean, msg: string) => (show ? <p className="mt-1 text-xs text-danger-strong" role="alert">{msg}</p> : null);

  return (
    <div className="space-y-5">
      <p className="flex gap-2 rounded-xl bg-muted p-4 text-sm text-muted-foreground">
        <Building2 className="h-4 w-4 shrink-0" aria-hidden="true" />{t('company.intro')}
      </p>

      {canPurchase && !creating && (
        <Button onClick={() => setCreating(true)}><Plus className="h-4 w-4" />{t('company.new')}</Button>
      )}

      {creating && (
        <Card>
          <CardHeader><CardTitle className="text-base">{t('company.new')}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="co-entity" className="mb-1.5 block">{t('company.payingEntity')}</Label>
                <Select value={entityId} onValueChange={setEntityId}>
                  <SelectTrigger id="co-entity"><SelectValue placeholder={t('company.chooseEntity')} /></SelectTrigger>
                  <SelectContent>{entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
                {err(showErrors && !entityId, t('company.errors.entity'))}
              </div>
              <div>
                <Label htmlFor="co-cc" className="mb-1.5 block">{t('company.costCentre')}</Label>
                <Input id="co-cc" value={costCentre} maxLength={100} onChange={(e) => setCostCentre(e.target.value)} />
              </div>
            </div>
            <div>
              <Label htmlFor="co-purpose" className="mb-1.5 block">{t('company.purpose')}</Label>
              <Textarea id="co-purpose" value={purpose} maxLength={500} onChange={(e) => setPurpose(e.target.value)} />
              {err(showErrors && purpose.trim().length < 3, t('company.errors.purpose'))}
            </div>

            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-foreground">{t('company.recipients')}</legend>
              {lines.map((l, i) => (
                <div key={l.key} className="space-y-3 rounded-xl border border-border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <Select value={l.kind} onValueChange={(v) => setLine(l.key, { kind: v as LineDraft['kind'], person: null })}>
                      <SelectTrigger className="w-48" aria-label={t('company.recipientKind', { n: i + 1 })}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employee">{t('company.kind.employee')}</SelectItem>
                        <SelectItem value="external">{t('company.kind.external')}</SelectItem>
                      </SelectContent>
                    </Select>
                    {lines.length > 1 && (
                      <Button size="sm" variant="ghost" onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} aria-label={t('company.removeLine', { n: i + 1 })}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {l.kind === 'employee' ? (
                    <PeoplePicker id={`co-person-${l.key}`} value={l.person} onChange={(p) => setLine(l.key, { person: p })}
                      fetchPage={(q, page) => listEligibleAssignees({ q, page, purpose: 'collaborate' })}
                      placeholder={t('company.pickEmployee')} aria-label={t('company.pickEmployee')} />
                  ) : (
                    <Input value={l.label} maxLength={40} onChange={(e) => setLine(l.key, { label: e.target.value })} placeholder={t('recipient.namePlaceholder')} aria-label={t('recipient.name')} />
                  )}
                  <div className="grid gap-3 sm:grid-cols-3">
                    <PhoneInput id={`co-phone-${l.key}`} value={l.phone} onChange={(v) => setLine(l.key, { phone: v })} />
                    <Select value={l.operator} onValueChange={(v) => setLine(l.key, { operator: v })}>
                      <SelectTrigger aria-label={t('recipient.theirNetwork')}><SelectValue placeholder={t('form.networkPlaceholder')} /></SelectTrigger>
                      <SelectContent>{operators.map((o) => <SelectItem key={o.code} value={o.code}>{o.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input inputMode="numeric" value={l.amount} onChange={(e) => setLine(l.key, { amount: e.target.value.replace(/\D/g, '') })} placeholder={t('company.airtimeAmount')} aria-label={t('company.airtimeAmount')} />
                  </div>
                  {err(showErrors && !lineOk(l), t('company.errors.line'))}
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setLines((ls) => [...ls, newLine()])}><Plus className="h-4 w-4" />{t('company.addRecipient')}</Button>
            </fieldset>

            <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-foreground">{t('company.total')}: <span className="font-semibold tabular-nums">{money(total)}</span></p>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setCreating(false)}>{t('confirm.cancel')}</Button>
                <Button onClick={create} disabled={busy === 'create'}>{t('company.saveDraft')}</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {error ? (
        <Card><CardContent className="p-0"><DataState variant={errorVariant(error)} title={t('company.loadError')} message={getApiErrorMessage(error, t('company.loadError'))} onRetry={load} /></CardContent></Card>
      ) : list === null ? (
        <Card><CardContent className="p-0"><DataState variant="loading" /></CardContent></Card>
      ) : list.length === 0 ? (
        <Card><CardContent className="p-0"><DataState variant="empty" title={t('company.emptyTitle')} message={t('company.empty')} /></CardContent></Card>
      ) : (
        <ul className="space-y-3">
          {list.map((r) => {
            const mine = !!currentUserId && r.requesterId === currentUserId;
            return (
              <li key={r.id}>
                <Card>
                  <CardContent className="space-y-3 p-5">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-foreground">{r.purpose}</p>
                        <p className="text-sm text-muted-foreground">{entityName(r.entityId)}{r.costCentre ? ` · ${r.costCentre}` : ''}</p>
                      </div>
                      <Badge variant={STATUS_BADGE[r.status] ?? 'neutral'} dot>{t(`company.status.${r.status}`, r.status)}</Badge>
                    </div>
                    <p className="text-sm text-foreground">
                      {t('company.lineSummary', { count: r.summary.lines, total: money(r.total, r.currency) })}
                    </p>
                    <ul className="divide-y divide-border text-sm">
                      {r.lines.map((l) => (
                        <li key={l.id} className="flex justify-between gap-3 py-1.5">
                          <span className="text-foreground">{l.recipientLabel || t(`company.kind.${l.recipientKind}`)} <span className="text-muted-foreground tabular-nums">{l.recipient}</span></span>
                          <span className="tabular-nums text-foreground">{money(l.amount, r.currency)}</span>
                        </li>
                      ))}
                    </ul>
                    {r.decisionNote && <p className="text-xs text-muted-foreground">{t('company.note')}: {r.decisionNote}</p>}
                    {r.status === 'approved' && r.executionBlockedReason && (
                      <p className="flex gap-2 rounded-lg bg-muted p-3 text-xs text-muted-foreground"><Lock className="h-4 w-4 shrink-0" aria-hidden="true" />{t('company.gated')}</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {mine && r.status === 'draft' && <Button size="sm" onClick={() => act(r, 'submit')} disabled={!!busy}>{t('company.submit')}</Button>}
                      {canApprove && !mine && r.status === 'submitted' && (
                        <>
                          <Button size="sm" onClick={() => act(r, 'approve')} disabled={!!busy}>{t('company.approve')}</Button>
                          <Button size="sm" variant="outline" onClick={() => act(r, 'reject')} disabled={!!busy}>{t('company.reject')}</Button>
                        </>
                      )}
                      {mine && ['draft', 'submitted', 'approved'].includes(r.status) && (
                        <Button size="sm" variant="ghost" onClick={() => act(r, 'cancel')} disabled={!!busy}>{t('company.cancel')}</Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CompanyPurchasesPanel;
